// Message box with Send and the big mic button (prototype .composer). Tap the mic and speak: the LISTENING panel
// shows the words as they are heard; it sends by itself after a pause, or tap the mic again. The small language
// button switches what the mic listens for (বাংলা / English). The paperclip attaches files (a customer's PO as
// PDF / Excel / photo); they show as chips until sent.
import { useEffect, useRef, useState } from 'react';
import { LISTEN_LANGS } from '../useVoice';

export const MicIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
  </svg>
);

const MAX_FILES = 3;
const MAX_BYTES = 5 * 1024 * 1024;
const extOf = (name) => { const m = String(name).match(/\.([a-z0-9]{1,5})$/i); return (m ? m[1] : 'file').toUpperCase(); };
const sizeLabel = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const readFile = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).replace(/^data:[^;]*;base64,/, ''));
  r.onerror = () => reject(new Error(`Could not read ${file.name}`));
  r.readAsDataURL(file);
});

// Unsent text and files, kept per employee: a half-typed message stays with the person it was meant for.
const drafts = {};

export default function Composer({ agentId, agentName, onSend, onMic, onCancelListen, listening, transcript, disabled, lang, onLang, onError }) {
  const [text, setText] = useState(() => (drafts[agentId] || {}).text || '');
  const [files, setFiles] = useState(() => (drafts[agentId] || {}).files || []); // [{ name, type, size, ext, data }]
  const picker = useRef(null);
  useEffect(() => {
    if (text || files.length) drafts[agentId] = { text, files };
    else delete drafts[agentId];
  }, [agentId, text, files]);

  const addFiles = async (list) => {
    const picked = Array.from(list || []);
    for (const f of picked) {
      if (files.length + picked.indexOf(f) >= MAX_FILES) { onError && onError(`Attach at most ${MAX_FILES} files at a time.`); break; }
      if (f.size > MAX_BYTES) { onError && onError(`"${f.name}" is ${sizeLabel(f.size)}. Files up to 5 MB, please.`); continue; }
      try {
        const data = await readFile(f);
        setFiles((x) => [...x, { name: f.name, type: f.type, size: sizeLabel(f.size), ext: extOf(f.name), data }].slice(0, MAX_FILES));
      } catch (err) { onError && onError(err.message); }
    }
    if (picker.current) picker.current.value = '';
  };

  const submit = (e) => {
    e.preventDefault();
    const v = text.trim();
    if ((!v && !files.length) || disabled) return;
    setText('');
    const sending = files;
    setFiles([]);
    onSend(v, false, sending);
  };
  const nextLang = lang === 'bn-BD' ? 'en-US' : 'bn-BD';

  return (
    <div className="composer">
      {listening && (
        <div className="listen">
          <div className="lab">LISTENING · {LISTEN_LANGS[lang] || lang}</div>
          <div className="wave">{Array.from({ length: 9 }, (_, i) => <i key={i} />)}</div>
          <div className="tr">{transcript || <span className="tr-hint">Speak now…</span>}</div>
          <div className="listen-a">
            <button type="button" className="aio-btn pri" onClick={onMic}>Done, send</button>
            <button type="button" className="aio-btn ghost" onClick={onCancelListen}>Cancel</button>
          </div>
        </div>
      )}
      {!listening && files.length > 0 && (
        <div className="filechips">
          {files.map((f, i) => (
            <span className="filechip" key={i}><i>{f.ext}</i>{f.name} · {f.size}
              <button type="button" aria-label={`Remove ${f.name}`} onClick={() => setFiles((x) => x.filter((_, j) => j !== i))}>×</button>
            </span>
          ))}
        </div>
      )}
      {!listening && (
        <form className={`cf ${text.trim() || files.length ? '' : 'empty'}`} autoComplete="off" onSubmit={submit}
          onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}>
          <input ref={picker} type="file" hidden multiple accept=".pdf,.xlsx,.xls,.xlsm,.csv,.txt,image/png,image/jpeg,image/webp" onChange={(e) => addFiles(e.target.files)} />
          <button type="button" className="clipbtn" title="Attach a file (PO as PDF, Excel or photo)" aria-label="Attach a file" onClick={() => picker.current && picker.current.click()} disabled={disabled}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5l-8.6 8.6a5 5 0 0 1-7.1-7.1l8.6-8.6a3.5 3.5 0 0 1 5 5l-8.6 8.6a2 2 0 0 1-2.8-2.8l7.9-7.9" /></svg>
          </button>
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder={disabled && agentName ? `${agentName} is answering…` : 'Type or tap the mic'} title="Type a message, or tap the mic" aria-label="Message" />
          <button className="send" type="submit" disabled={disabled}>Send</button>
          {onLang && (
            <button type="button" className="langbtn" onClick={() => onLang(nextLang)} title={`Mic listens for ${LISTEN_LANGS[lang]}. Tap for ${LISTEN_LANGS[nextLang]}.`} aria-label={`Mic language: ${LISTEN_LANGS[lang]}`}>
              {lang === 'bn-BD' ? 'বাং' : 'EN'}
            </button>
          )}
          <button className="mic" type="button" aria-label="Tap to talk" onClick={onMic} disabled={disabled}><MicIcon /></button>
        </form>
      )}
    </div>
  );
}
